import * as React from "react";
import { useEffect } from "react";
import { Navigate, Outlet, useNavigate, useRoutes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import "@utils/i18n";
import { useAppSelector } from "@store/hooks";
import { AppDispatch, RootState } from "@src/store";
import DashboardLayout from "@layouts/dashboard";
import ChooseCategoyLayout from "@layouts/choose-category";
import Login from "@pages/auth/Login";
import { NieuwePrints } from "@pages/dashboard/NieuwePrints";
import NoMatch from "@pages/PageNotFound";
import BevestigUpload from "@pages/dashboard/BevestigUpload";
import MijnWachtrij from "@pages/dashboard/MijnWachtrij";
import RecentePrints from "@pages/dashboard/RecentePrints";
import BeheerPrints from "@pages/dashboard/BeheerPrint";
import MijnReservaties from "@pages/dashboard/MijnReservaties";
import CreatePCReservation from "@pages/dashboard/CreatePCReservation";
import AnimatedPage from "@components/animated-page";
import CreateWorkPlaceReservation from "@pages/dashboard/CreateWorkPlaceReservation";
import CreateMaterialReservation from "@pages/dashboard/CreateMaterialReservation";
import LoginWorkplace from "@pages/auth/LoginWorkplace";
import ChooseCategory from "@pages/dashboard/ChooseCategory";
import { getWebsiteSettings } from "@features/website-settings/action";
import { useDispatch } from "react-redux";
import { useAuth } from "@hooks/useAuth";

const getDefaultRedirectRoute = (lang: string, userOptions: any, t: any) => {
  const available = userOptions && Object.entries(userOptions)
      .filter(([key, value]) => key !== "_id" && key !== "sessionToken" && value === true);

  if (!available || available.length === 0) return "/";

  if (available.length > 1) return `/${lang}/${t("router.choose-category")}`;
  if (userOptions?.BYODAvailable) return `/${lang}/${t("router.nieuwe-prints")}`;
  if (userOptions?.reservePCAvailable) return `/${lang}/${t("router.create-pc-reservation")}`;
  if (userOptions?.reservePlaceAvailable) return `/${lang}/${t("router.create-workplace-reservation")}`;

  return "/";
};

const PrivateOutlet = () => {
  const { userToken } = useAppSelector((state: RootState) => state.auth);
  return userToken ? <Outlet /> : <Navigate to={`/${i18n.language}/login`} />;
};

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
  const { userToken } = useAppSelector((state: RootState) => state.auth);
  return userToken ? children : <Navigate to={`/${i18n.language}/login`} />;
};

const Router = () => {
  const { t } = useTranslation();
  const lang = i18n.language;
  const { userOptions } = useAuth();
  const { userToken } = useAppSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(getWebsiteSettings());
  }, [dispatch]);

  const dashboardChildrenRoutes = [
    { path: t("router.nieuwe-prints"), component: <NieuwePrints /> },
    { path: t("router.mijn-wachtrij"), component: <MijnWachtrij /> },
    { path: t("router.recente-prints"), component: <RecentePrints /> },
    { path: t("router.bevestig-upload"), component: <BevestigUpload /> },
    { path: t("router.beheer-print"), component: <BeheerPrints /> },
    { path: t("router.create-pc-reservation"), component: <CreatePCReservation /> },
    { path: t("router.create-workplace-reservation"), component: <CreateWorkPlaceReservation /> },
    { path: t("router.create-material-reservation"), component: <CreateMaterialReservation /> },
    { path: t("router.mijn-reservaties"), component: <MijnReservaties /> },
  ];

  const dashboardRoutes = {
    path: `/${lang}`,
    element: <PrivateOutlet />,
    children: [
      {
        index: true,
        element: <Navigate to={getDefaultRedirectRoute(lang, userOptions, t)} />,
      },
      ...dashboardChildrenRoutes.map(({ path, component }) => ({
        path,
        element: (
            <DashboardLayout>
              <PrivateRoute>{component}</PrivateRoute>
            </DashboardLayout>
        ),
      })),
      {
        path: t("router.choose-category"),
        element: (
            <ChooseCategoyLayout>
              <PrivateRoute>
                <ChooseCategory />
              </PrivateRoute>
            </ChooseCategoyLayout>
        ),
      },
    ],
  };

  const routes = [
    { path: "/", element: <Navigate to={`/${lang}`} /> },
    { path: `/${lang}/login`, element: <Login /> },
    { path: `/${lang}/${t("router.signin-workplace")}/:objectName`, element: <LoginWorkplace /> },
    dashboardRoutes,
    {
      path: "*",
      element: (
          <AnimatedPage animationType="FadeIn">
            <NoMatch />
          </AnimatedPage>
      ),
    },
  ];





  const routing = useRoutes(routes);

  if (!lang) {
    return null;
  }

  return routing;
};

export default Router;